/**
 * Cart — slide-out drawer from the right showing the current cart contents,
 * subtotal, shipping estimate, and a "Checkout" button that posts to the
 * Stripe-backed `/api/shop/checkout` endpoint.
 *
 * State lives in /lib/cart.js (localStorage-backed). Render this once near
 * the top of the tree (Layout). Other components open it via `useCart().openCart()`
 * (e.g. when "Buy Now" / "Add to Cart" is clicked).
 */
import { useState } from "react";
import { X, ShoppingBag, Plus, Minus, Trash2, ShieldCheck, Truck } from "lucide-react";
import { useCart, CART_BASE_SHIPPING } from "../lib/cart";
import { api } from "../lib/api";
import logger from "../lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImage = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BACKEND_URL}${path}`;
};

const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

export default function Cart() {
  const {
    items,
    count,
    subtotal,
    shippingEstimate,
    totalEstimate,
    removeItem,
    updateQuantity,
    isOpen,
    closeCart,
  } = useCart();

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleCheckout = async () => {
    if (items.length === 0 || submitting) return;
    setSubmitting(true);
    setErrorMsg("");
    try {
      const { url } = await api.createCheckoutSession(items, window.location.origin);
      if (url) {
        window.location.href = url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Checkout failed";
      logger.error("Stripe checkout failed:", detail);
      setErrorMsg(typeof detail === "string" ? detail : "Checkout failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close cart"
        onClick={closeCart}
        className={`fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm transition-opacity duration-300
          ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        data-testid="cart-backdrop"
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
        className={`fixed top-0 right-0 z-[61] h-full w-full sm:w-[420px] max-w-full
          bg-demolition-black border-l-2 border-demolition-red/60 shadow-2xl
          flex flex-col transform transition-transform duration-300 ease-out
          ${isOpen ? "translate-x-0" : "translate-x-full"}`}
        data-testid="cart-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 bg-demolition-paper">
          <div className="flex items-center gap-3">
            <ShoppingBag className="w-6 h-6 text-demolition-red" />
            <div>
              <h2
                className="font-russo text-xl text-white uppercase tracking-tighter leading-none"
                data-testid="cart-title"
              >
                YOUR CART
              </h2>
              <p className="font-teko text-sm text-demolition-chrome uppercase tracking-widest mt-1">
                {count === 0 ? "Empty" : `${count} item${count === 1 ? "" : "s"}`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeCart}
            className="p-2 text-white/70 hover:text-demolition-red transition-colors"
            aria-label="Close cart"
            data-testid="cart-close-button"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto" data-testid="cart-body">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center px-8 py-16 text-center" data-testid="cart-empty">
              <ShoppingBag className="w-14 h-14 text-white/20 mb-4" />
              <p className="font-russo text-2xl text-white uppercase tracking-tighter mb-2">
                Your cart is empty
              </p>
              <p className="font-mono text-sm text-white/50">
                Browse the Shop and add some official Demolition merch.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {items.map((li) => (
                <li
                  key={`${li.item_id}::${li.variation_id || "_"}`}
                  className="flex gap-4 px-6 py-5"
                  data-testid={`cart-line-${li.item_id}-${li.variation_id || "default"}`}
                >
                  {li.thumbnail_url ? (
                    <img
                      src={resolveImage(li.thumbnail_url)}
                      alt={li.name}
                      className="w-20 h-20 flex-shrink-0 object-cover border border-demolition-red/30"
                    />
                  ) : (
                    <div className="w-20 h-20 flex-shrink-0 bg-demolition-red/10 border border-demolition-red/40 flex items-center justify-center">
                      <ShoppingBag className="w-7 h-7 text-demolition-red" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="font-russo text-base text-white uppercase tracking-tight leading-tight break-words">
                          {li.name}
                        </h3>
                        {li.variation_name && (
                          <p className="font-mono text-xs text-demolition-chrome mt-0.5 truncate">
                            {li.variation_name}
                            {li.sku ? ` · SKU ${li.sku}` : ""}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(li.item_id, li.variation_id)}
                        className="text-white/40 hover:text-demolition-red transition-colors p-1 -m-1 flex-shrink-0"
                        aria-label={`Remove ${li.name}`}
                        data-testid={`cart-remove-${li.item_id}-${li.variation_id || "default"}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="font-mono text-sm text-demolition-chrome mt-1">
                      {fmt(li.price)} each
                    </p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-white/15">
                        <button
                          type="button"
                          onClick={() => updateQuantity(li.item_id, li.quantity - 1, li.variation_id)}
                          disabled={li.quantity <= 1}
                          className="px-2 py-1 text-white/80 hover:text-demolition-red disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          aria-label="Decrease quantity"
                          data-testid={`cart-qty-decrease-${li.item_id}-${li.variation_id || "default"}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span
                          className="px-3 py-1 font-mono text-sm text-white border-x border-white/15 min-w-[2.5rem] text-center"
                          data-testid={`cart-qty-${li.item_id}-${li.variation_id || "default"}`}
                        >
                          {li.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(li.item_id, li.quantity + 1, li.variation_id)}
                          disabled={li.max_quantity && li.quantity >= li.max_quantity}
                          className="px-2 py-1 text-white/80 hover:text-demolition-red disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          aria-label="Increase quantity"
                          data-testid={`cart-qty-increase-${li.item_id}-${li.variation_id || "default"}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="font-russo text-base text-white">
                        {fmt(li.price * li.quantity)}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-white/10 bg-demolition-paper px-6 py-5 space-y-4" data-testid="cart-footer">
            <div className="space-y-2 font-mono text-sm">
              <div className="flex justify-between text-white/70">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">{fmt(subtotal)}</span>
              </div>
              <div className="flex justify-between text-white/70">
                <span className="flex items-center gap-1.5">
                  <Truck className="w-3.5 h-3.5" />
                  Shipping (US)
                </span>
                <span data-testid="cart-shipping">{fmt(shippingEstimate)}</span>
              </div>
              <div className="flex justify-between text-white/40 text-xs italic">
                <span>Tax</span>
                <span>Calculated at checkout</span>
              </div>
              <div className="border-t border-white/10 pt-2 mt-2 flex justify-between items-baseline">
                <span className="font-russo text-white uppercase tracking-tight text-base">Total</span>
                <span className="font-russo text-demolition-red text-2xl" data-testid="cart-total">
                  {fmt(totalEstimate)}
                </span>
              </div>
            </div>

            {errorMsg && (
              <div
                className="border border-demolition-red/60 bg-demolition-red/10 px-3 py-2 font-mono text-xs text-demolition-red"
                data-testid="cart-error"
              >
                {errorMsg}
              </div>
            )}

            <button
              type="button"
              onClick={handleCheckout}
              disabled={submitting}
              className="w-full bg-demolition-red hover:bg-demolition-red/85 disabled:bg-demolition-red/40 disabled:cursor-not-allowed
                text-white font-russo uppercase tracking-widest text-lg py-3
                transition-colors border-2 border-demolition-red flex items-center justify-center gap-2"
              data-testid="cart-checkout-button"
            >
              {submitting ? "Redirecting..." : "Checkout"}
            </button>

            <p className="font-mono text-[11px] text-white/40 leading-relaxed flex items-start gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-demolition-chrome mt-0.5 flex-shrink-0" />
              <span>
                Secure payment via Stripe. US shipping only — flat {fmt(CART_BASE_SHIPPING)} (or item override, whichever is higher).
                Sales tax added at checkout where applicable.
              </span>
            </p>
          </div>
        )}
      </aside>
    </>
  );
}
